<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{
      zIndex: '1100',
    }"
    @hide="handleHideTasteModal"
    :pt="{
      root: {
        class: 'taste-modal-root'
      },
    }"
  >
    <template #container="{ closeCallback }">
      <div
        class="taste-modal-container"
        :style="{
          background:
            (currentStep?.type === 'welcome' && !isDesktop)
              ? `linear-gradient(180deg,rgba(171, 183, 246, 0.5) 0%,rgba(208, 203, 255, 0) 100%)`
              : `#FFFFFF`,
          backgroundSize: 'auto 50%',
          backgroundPosition:'top center',
          backgroundRepeat:'no-repeat',
        }"
      >
        <!-- Loading State -->
        <div v-if="tasteStore.tasteModal.isLoadingConfig" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Загрузка опроса...</p>
        </div>

        <template v-else>
          <!-- Header -->
          <div class="taste-header">
            <div class="taste-header-top">
              <div class="taste-header-left">
                <custom-button
                  @click="closeCallback"
                  class="save-exit-button"
                  severity="info"
                >
                  <i class="pi pi-chevron-left"></i>
                  {{ isWelcomeStep ? "Назад" : "Сохранить и выйти" }}
                </custom-button>
              </div>

              <div class="taste-header-center">
                <div v-if="isWelcomeStep" class="taste-title title">
                  {{ configTitle }}
                </div>
              </div>

              <div class="taste-header-right">
                <custom-button
                  @click="closeCallback"
                  class="save-exit-button-right "
                  severity="info"
                >
                  {{ isWelcomeStep ? "" : "Сохранить и выйти" }}
                  <i class="pi pi-times"></i>
                </custom-button>
              </div>
            </div>
            <div class="taste-header-bottom">
              <div v-if="!isWelcomeStep && !isStyleEvaluationgChoiceStep && !isResultsChoiceStep && currentStep" 
              class="progress-container">
                <div class="progress-bar">
                  <div
                    class="progress-fill"
                    :style="{ width: `${progressPercentage}%` }"
                  ></div>
                </div>
                <!-- Swipe step: the counter is the DECK's, and only once the deck is known.
                     The step counter ("1 из 5") here read as cards on a slow connection. -->
                <span v-if="isTinderLikeStep && swipeProgress && swipeProgress.total_cards > 0" class="progress-text helper-text">
                  {{ swipeProgress.swiped_in_session }} из {{ swipeProgress.total_cards }}
                </span>
                <span v-else-if="isTinderLikeStep && swipeProgress" class="progress-text helper-text">Нет образов</span>
                <span v-else-if="isTinderLikeStep" class="progress-text helper-text">Загрузка…</span>
                <span v-else class="progress-text helper-text">{{ currentStepIndex + 1 }} из {{ totalSteps }}</span>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="taste-content">
            <DynamicStepRenderer />
          </div>

          <!-- Footer -->
          <div 
            v-if="showFooter" 
            class="taste-footer"
            :class="{ 
              'has-saved-indicator': showSavedIndicator || currentStep?.type === 'tinder_like',
              'tinder-like-footer': currentStep?.type === 'tinder_like'
            }"
          >
            <!-- Saved indicator for steps with data -->
            <div v-if="showSavedIndicator" class="answers-saved-indicator">
              <span class="answers-saved-text">Ответы сохранены</span>
              <i class="pi pi-bookmark answers-saved-icon"></i>
            </div>
            
            <!-- For tinder_like step: Only saved indicator -->
            <template v-else-if="currentStep?.type === 'tinder_like'">
              <div class="answers-saved-indicator">
                <span class="answers-saved-text">Данные сохранены</span>
                <i class="pi pi-bookmark answers-saved-icon"></i>
              </div>
            </template>
            
            <!-- Buttons container -->
            <div class="footer-buttons" :class="`${currentStep.id}-footer-buttons`">
              <!-- For results-choice step: Evaluate More and View Results buttons -->
              <template v-if="isResultsChoiceStep">
                <custom-button
                  @click="handleEvaluateMore"
                  :severity="isDesktop ? 'info' : 'danger'"
                  rounded
                  class="evaluate-more-btn"
                  label="Оценить ещё 15 образов"
                />
                <custom-button
                  @click="handleViewResults"
                  class="view-results-btn"
                  label="Посмотреть результаты"
                  rounded
                />
              </template>
              
              <!-- For later step: Back and Go to Profile buttons -->
              <template v-else-if="currentStep?.type === 'later'">
                <custom-button
                  @click="handleBackFromLater"
                  severity="info"
                  rounded
                  class="back-btn"
                  label="Назад"
                />
                <custom-button
                  @click="handleGoToProfile"
                  class="next-btn"
                  label="Перейти в профиль"
                  rounded
                />
              </template>
              
              <!-- For choice step: Later and Start buttons -->
              <template v-else-if="currentStep?.type === 'choice'">
                <custom-button
                  v-if="showLaterButton"
                  @click="handleLater"
                  :severity="isDesktop ? 'info' : 'danger'"
                  rounded
                  class="later-btn"
                  label="Позже"
                />
                <custom-button
                  v-if="showNextButton"
                  @click="handleNext"
                  class="next-btn"
                  :label="nextButtonLabel"
                  :loading="tasteStore.tasteModal.isLoading"
                  :disabled="!tasteStore.validateCurrentStep()"
                  rounded
                />
              </template>
              
              <!-- For other steps: Back and Next buttons -->
              <template v-else>
                <custom-button
                  v-if="showBackButton"
                  @click="prevStep"
                  :severity="isDesktop ? 'info' : 'danger'"
                  rounded
                  class="back-btn"
                  label="Назад"
                />
                <custom-button
                  v-if="showNextButton"
                  @click="handleNext"
                  class="next-btn"
                  :label="nextButtonLabel"
                  :loading="tasteStore.tasteModal.isLoading"
                  :disabled="!tasteStore.validateCurrentStep()"
                  rounded
                />
              </template>
            </div>
          </div>
        </template>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import Dialog from "primevue/dialog";

import CustomButton from "@/components/UI/CustomButtom/CustomButton.vue";

import { useTasteClarificationModal } from "@/features/taste/store";
import DynamicStepRenderer from "./TasteSteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const tasteStore = useTasteClarificationModal();
const router = useRouter();
const tasteModal = computed(() => tasteStore.tasteModal);
const currentStep = computed(() => tasteStore.currentStep);
const currentStepIndex = computed(() => tasteStore.tasteModal.currentStepIndex);
const totalSteps = computed(() => tasteStore.totalSteps);
const { isDevice } = useDevice()
const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);
const savedSteps = ref<Set<string>>(new Set());
const previousStepId = ref<string | null>(null);

const configTitle = computed(() => {
  return tasteStore.tasteModal.config?.title || "Уточнение вкусов";
});

const isWelcomeStep = computed(() => {
  return currentStep.value?.type === "welcome";
});

const isResultsChoiceStep = computed(() => {
  return currentStep.value?.id === "results-choice";
});


const isStyleEvaluationgChoiceStep = computed(() => {
  return currentStep.value?.id === "style-evaluation-choice" || currentStep.value?.type === "style_evaluation";
});

const isTinderLikeStep = computed(() => {
  return currentStep.value?.type === 'tinder_like';
});

const swipeProgress = computed(() => {
  if (!isTinderLikeStep.value || !currentStep.value) return null;
  const progressKey = `${currentStep.value.id}_sessionProgress`;
  const progress = tasteStore.tasteModal.tasteData[progressKey];
  return progress || null;
});

const progressPercentage = computed(() => {
  // Если это tinder_like шаг и есть данные о свайпах, показываем процент свайпов
  if (isTinderLikeStep.value && swipeProgress.value) {
    if (swipeProgress.value.total_cards === 0) return 0;
    return (swipeProgress.value.swiped_in_session / swipeProgress.value.total_cards) * 100;
  }
  // Иначе показываем обычный прогресс по шагам
  if (totalSteps.value === 0) return 0;
  return ((currentStepIndex.value + 1) / totalSteps.value) * 100;
});

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide back button on welcome step
  if (stepType === "welcome") return false;

  if (stepType === "gender" ) return false;
  
  // Show back button for later step
  if (stepType === "later") return true;
  
  // Hide back button on steps that handle navigation themselves
  if (stepType === "choice" || stepType === "tinder_like" || stepType === "style_evaluation") return false;
  
  return currentStepIndex.value > 0;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide next button for results-choice step (buttons are in footer)
  if (isResultsChoiceStep.value) return false;
  
  // Show next button for choice step (as "Начать")
  if (stepType === "choice") return true;
  
  // Hide next button on steps that handle navigation themselves
  if (stepType === "thank_you" || stepType === "tinder_like" || stepType === "later" || stepType === "style_evaluation") {
    return false;
  }
  
  return currentStepIndex.value < totalSteps.value - 1;
});

const showLaterButton = computed(() => {
  if (!currentStep.value) return false;
  // Don't show later button for results-choice step
  if (isResultsChoiceStep.value) return false;
  return currentStep.value.type === "choice";
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === "welcome" || currentStep.value.type === "choice") {
    return "Начать";
  }
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Show footer for results-choice, choice and later steps
  if (isResultsChoiceStep.value || stepType === "choice" || stepType === "later") {
    return true;
  }
  
  // Hide footer on steps that handle navigation themselves
  if (stepType === "thank_you") {
    return false;
  }
  
  return true;
});

const showSavedIndicator = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const stepId = currentStep.value.id;
  
  // Не показываем плашку для исключенных шагов
  if (stepType === "welcome" || stepType === "later" || stepType === "thank_you" || stepType === "tinder_like") {
    return false;
  }
  
  // Показываем если есть данные для текущего шага и шаг в списке сохраненных
  const hasData = tasteStore.tasteModal.tasteData[stepId] !== undefined && 
                  tasteStore.tasteModal.tasteData[stepId] !== null &&
                  (Array.isArray(tasteStore.tasteModal.tasteData[stepId]) 
                    ? tasteStore.tasteModal.tasteData[stepId].length > 0
                    : true);
  
  return hasData && savedSteps.value.has(stepId);
});

function handleHideTasteModal() {
  tasteStore.hideTasteModal();
}

function prevStep() {
  tasteStore.prevStep();
}

function handleNext() {
  if (!tasteStore.validateCurrentStep()) {
    return;
  }
  
  tasteStore.nextStep();
}

function handleLater() {
  tasteStore.showLaterStep();
}

function handleGoToProfile() {
  tasteStore.hideTasteModal();
  router.push('/profile');
}

function handleBackFromLater() {
  tasteStore.hideLaterStep();
}

function handleEvaluateMore() {
  // Сохраняем выбор "evaluate_more" для валидации шага
  if (currentStep.value) {
    tasteStore.updateTasteData(currentStep.value.id, 'evaluate_more');
  }
  tasteStore.nextStep();
}

function handleViewResults() {
  tasteStore.hideTasteModal();
  router.push('/profile/my-style');
}

watch(
  () => tasteModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
    if (!newVal) {
      // Сбрасываем состояние при закрытии модального окна
      savedSteps.value.clear();
      previousStepId.value = null;
    }
  }
);

// Отслеживаем изменения в tasteData и добавляем ID шага в список сохраненных
watch(
  () => tasteStore.tasteModal.tasteData,
  (newData) => {
    if (!currentStep.value) return;
    const stepId = currentStep.value.id;
    const stepType = currentStep.value.type;
    
    // Не отслеживаем исключенные шаги
    if (stepType === "welcome" || stepType === "later" || stepType === "thank_you" || stepType === "tinder_like") {
      return;
    }
    
    // Проверяем, есть ли данные для текущего шага
    const hasData = newData[stepId] !== undefined && 
                    newData[stepId] !== null &&
                    (Array.isArray(newData[stepId]) 
                      ? newData[stepId].length > 0
                      : true);
    
    if (hasData) {
      savedSteps.value.add(stepId);
    }
  },
  { deep: true }
);

// Отслеживаем смену шага и проверяем, есть ли уже данные для нового шага
watch(
  () => currentStep.value?.id,
  (newStepId) => {
    if (!newStepId || !currentStep.value) return;
    
    const stepType = currentStep.value.type;
    
    // Не обрабатываем исключенные шаги
    if (stepType === "welcome" || stepType === "later" || stepType === "thank_you" || stepType === "tinder_like") {
      previousStepId.value = newStepId;
      return;
    }
    
    // Проверяем, есть ли уже данные для этого шага
    const stepData = tasteStore.tasteModal.tasteData[newStepId];
    const hasData = stepData !== undefined && 
                    stepData !== null &&
                    (Array.isArray(stepData) ? stepData.length > 0 : true);
    
    if (hasData) {
      savedSteps.value.add(newStepId);
    }
    
    previousStepId.value = newStepId;
  }
);
</script>

<style>
.taste-modal-root {
  width: 60vw;
  max-width: 1020px;
  height: auto;
  max-height: 90vh;
  overflow: hidden;
}


.step-title {
  font-weight: 600 !important;
  color: var(--primary-blue-100) !important;
  letter-spacing: -1.92px !important;
}

.step-subtitle {
  color: var(--primary-blue-80) !important;
  letter-spacing: -0.8px !important;
}

@media (max-width: 1024px) {
  .taste-modal-root {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
  }

  .step-title {
    font-size: 32px !important;
    line-height: 1.09 !important;
    font-weight: 700 !important;
    letter-spacing: -0.64px !important;
    
  }

  .step-subtitle {
    font-size: 15px !important;
    line-height: 1.3 !important;
    letter-spacing: -0.9px !important;
  }
}
</style>

<style scoped>

.taste-modal-container {
  display: flex;
  flex-direction: column;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding:20px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.taste-title {
  display: none;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.taste-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  padding:20px 20px 0 20px;
}

.taste-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.taste-header-bottom {
  width: 100%;
}

.taste-header-left {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;

}
.taste-header-right {
  display: flex;
  height: 45px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 10px;
  flex: 1 0 0;
}

.taste-header-center {
  flex: 1;
  text-align: center;
}

.save-exit-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  gap: 10px;
  color: var(--active-100);
  cursor: pointer;
  transition: background-color 0.2s;
}


.save-exit-button-right {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  gap: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
  position: absolute;
  right: 8px;
  top: 8px;
}

.progress-container {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 4px;
  align-items: flex-start;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: var(--light-blue-20);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--active-100);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  color: var(--active-100);
}

.taste-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  align-self: stretch;
  
}

.taste-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  padding: 20px 40px 40px 40px;
}

.taste-footer.has-saved-indicator {
  justify-content: space-between;
}

.taste-footer.tinder-like-footer {
  justify-content: center !important;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.answers-saved-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: 160px;
  flex-shrink: 0;
}

.answers-saved-text {
  font-family: Inter;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.21;
  letter-spacing: -0.04em;
  color: #6E85CB;
  white-space: nowrap;
}

.answers-saved-icon {
  font-size: 16px;
  color: var(--active-100);
  flex-shrink: 0;
}

@media (max-width: 1024px) {
  .taste-modal-container {
    display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
  padding:0;
  }

  .back-btn {
  flex: 1;
}

.next-btn {
  flex: 1;
}

.later-btn {
  flex: 1;
}

.evaluate-more-btn {
  flex: 1;
}

.view-results-btn {
  flex: 1;
}

  .taste-header {
    padding: 20px 20px 0 20px;
  }

  .taste-title {
    display: block;
    white-space: nowrap;
  }

  .taste-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
  }

  .taste-header-bottom {
    margin-top:30px;
  }

  .taste-footer {
    flex:none;
    padding: 0 20px 20px 20px;
  }

  .taste-footer {
    flex-direction: column;
    gap: 20px;
    padding: 0 20px 20px 20px;
  }

  .taste-footer.tinder-like-footer {
    justify-content: center;
  }

  .answers-saved-indicator {
    width: 100%;
    justify-content: center;
    order: 1;
  }

  .footer-buttons {
    width: 100%;
    order: 2;
  }

  .results-choice-footer-buttons {
    flex-direction: column;
  }

  .taste-header-left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    flex: 1 0 0;
    white-space: nowrap;
  }

.taste-header-right {
  display: flex;
  height: 45px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 10px;
  flex: 1 0 0;
}


.save-exit-button-right {
  display: none;
}
}

/* Desktop styles - плашка слева, кнопки справа */
@media (min-width: 1024px) {
  .taste-footer {
    flex-direction: row;
    align-items: center;
  }

  .taste-footer.has-saved-indicator {
    justify-content: space-between;
  }

  .answers-saved-indicator {
    width: 160px;
  }

  .footer-buttons {
    flex-shrink: 0;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .taste-footer {
    padding: 20px 40px 40px 40px;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .taste-footer {
    padding: 20px 40px 40px 40px;
  }
}

@media (min-width: 1920px) {
  .taste-footer {
    padding: 20px 40px 40px 40px;
  }
}
</style>
